import React from 'react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, LineChart, Line } from 'recharts';
import { UsersIcon, ClockIcon, CheckCircleIcon, FileTextIcon, TrendingUpIcon, AlertCircleIcon } from 'lucide-react';
export function OperationalAnalyticsReport({
  filters,
  locale,
  report
}) {
  // Sample data for user productivity
  const userData = [{
    id: 'USR-001',
    name: 'John Smith',
    department: 'Finance',
    tasksCompleted: 145,
    avgProcessingTime: 2.3,
    exceptionRate: 3.2
  }, {
    id: 'USR-002',
    name: 'Sarah Johnson',
    department: 'Procurement',
    tasksCompleted: 178,
    avgProcessingTime: 1.8,
    exceptionRate: 2.1
  }, {
    id: 'USR-003',
    name: 'Michael Brown',
    department: 'Accounts Payable',
    tasksCompleted: 132,
    avgProcessingTime: 2.5,
    exceptionRate: 4.7
  }, {
    id: 'USR-004',
    name: 'Emily Davis',
    department: 'Finance',
    tasksCompleted: 156,
    avgProcessingTime: 2.0,
    exceptionRate: 2.8
  }, {
    id: 'USR-005',
    name: 'Robert Wilson',
    department: 'Procurement',
    tasksCompleted: 112,
    avgProcessingTime: 3.1,
    exceptionRate: 5.2
  }];
  // Processing time trend data
  const processingTimeTrendData = [{
    month: 'Jan',
    time: 3.2
  }, {
    month: 'Feb',
    time: 2.8
  }, {
    month: 'Mar',
    time: 2.5
  }, {
    month: 'Apr',
    time: 2.2
  }, {
    month: 'May',
    time: 1.9
  }, {
    month: 'Jun',
    time: 1.8
  }];
  // Document processing by type
  const documentProcessingData = [{
    type: 'Invoices',
    count: 280
  }, {
    type: 'Purchase Orders',
    count: 175
  }, {
    type: 'Requisitions',
    count: 120
  }, {
    type: 'Receipts',
    count: 95
  }, {
    type: 'Contracts',
    count: 45
  }];
  // System usage by module
  const moduleUsageData = [{
    module: 'AP',
    users: 42,
    transactions: 320
  }, {
    module: 'Procurement',
    users: 38,
    transactions: 280
  }, {
    module: 'Vendors',
    users: 25,
    transactions: 150
  }, {
    module: 'Reporting',
    users: 35,
    transactions: 210
  }, {
    module: 'Settings',
    users: 12,
    transactions: 45
  }];
  // Use report title and description if provided, otherwise use defaults
  const reportTitle = report?.name || 'User Productivity Report';
  const reportDescription = report?.description || 'Analysis of user productivity, process efficiency, and system performance';
  return <div className="p-6" data-id="element-15883">
      <div className="mb-8" data-id="element-15884">
        <h3 className="text-lg font-medium text-gray-900" data-id="element-15885">{reportTitle}</h3>
        <p className="mt-1 text-sm text-gray-500" data-id="element-15886">{reportDescription}</p>
      </div>
      {/* Key metrics */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8" data-id="element-15887">
        <div className="bg-white overflow-hidden shadow rounded-lg" data-id="element-15888">
          <div className="p-5" data-id="element-15889">
            <div className="flex items-center" data-id="element-15890">
              <div className="flex-shrink-0 bg-indigo-500 rounded-md p-3" data-id="element-15891">
                <UsersIcon className="h-6 w-6 text-white" data-id="element-15892" />
              </div>
              <div className="ml-5 w-0 flex-1" data-id="element-15893">
                <dl data-id="element-15894">
                  <dt className="text-sm font-medium text-gray-500 truncate" data-id="element-15895">
                    Active Users
                  </dt>
                  <dd data-id="element-15896">
                    <div className="text-lg font-medium text-gray-900" data-id="element-15897">42</div>
                  </dd>
                </dl>
              </div>
            </div>
          </div>
          <div className="bg-gray-50 px-5 py-3" data-id="element-15898">
            <div className="text-sm" data-id="element-15899">
              <a href="#" className="font-medium text-indigo-600 hover:text-indigo-500" data-id="element-15900">
                View all users
              </a>
            </div>
          </div>
        </div>
        <div className="bg-white overflow-hidden shadow rounded-lg" data-id="element-15901">
          <div className="p-5" data-id="element-15902">
            <div className="flex items-center" data-id="element-15903">
              <div className="flex-shrink-0 bg-green-500 rounded-md p-3" data-id="element-15904">
                <ClockIcon className="h-6 w-6 text-white" data-id="element-15905" />
              </div>
              <div className="ml-5 w-0 flex-1" data-id="element-15906">
                <dl data-id="element-15907">
                  <dt className="text-sm font-medium text-gray-500 truncate" data-id="element-15908">
                    Avg. Processing Time
                  </dt>
                  <dd data-id="element-15909">
                    <div className="text-lg font-medium text-gray-900" data-id="element-15910">
                      1.8 days
                    </div>
                  </dd>
                </dl>
              </div>
            </div>
          </div>
          <div className="bg-gray-50 px-5 py-3" data-id="element-15911">
            <div className="text-sm" data-id="element-15912">
              <a href="#" className="font-medium text-indigo-600 hover:text-indigo-500" data-id="element-15913">
                View processing details
              </a>
            </div>
          </div>
        </div>
        <div className="bg-white overflow-hidden shadow rounded-lg" data-id="element-15914">
          <div className="p-5" data-id="element-15915">
            <div className="flex items-center" data-id="element-15916">
              <div className="flex-shrink-0 bg-blue-500 rounded-md p-3" data-id="element-15917">
                <CheckCircleIcon className="h-6 w-6 text-white" data-id="element-15918" />
              </div>
              <div className="ml-5 w-0 flex-1" data-id="element-15919">
                <dl data-id="element-15920">
                  <dt className="text-sm font-medium text-gray-500 truncate" data-id="element-15921">
                    System Uptime
                  </dt>
                  <dd data-id="element-15922">
                    <div className="text-lg font-medium text-gray-900" data-id="element-15923">
                      99.97%
                    </div>
                  </dd>
                </dl>
              </div>
            </div>
          </div>
          <div className="bg-gray-50 px-5 py-3" data-id="element-15924">
            <div className="text-sm" data-id="element-15925">
              <a href="#" className="font-medium text-indigo-600 hover:text-indigo-500" data-id="element-15926">
                View system status
              </a>
            </div>
          </div>
        </div>
      </div>
      {/* Charts */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mb-8" data-id="element-15927">
        <div className="bg-white p-6 rounded-lg shadow" data-id="element-15928">
          <h4 className="text-base font-medium text-gray-900 mb-4" data-id="element-15929">
            Document Processing by Type
          </h4>
          <div className="h-80" data-id="element-15930">
            <ResponsiveContainer width="100%" height="100%" data-id="element-15931">
              <BarChart data={documentProcessingData} margin={{
              top: 20,
              right: 30,
              left: 20,
              bottom: 5
            }} data-id="element-15932">
                <CartesianGrid strokeDasharray="3 3" data-id="element-15933" />
                <XAxis dataKey="type" data-id="element-15934" />
                <YAxis data-id="element-15935" />
                <Tooltip data-id="element-15936" />
                <Bar dataKey="count" fill="#4f46e5" data-id="element-15937" />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>
        <div className="bg-white p-6 rounded-lg shadow" data-id="element-15938">
          <h4 className="text-base font-medium text-gray-900 mb-4" data-id="element-15939">
            Average Processing Time Trend (Days)
          </h4>
          <div className="h-80" data-id="element-15940">
            <ResponsiveContainer width="100%" height="100%" data-id="element-15941">
              <LineChart data={processingTimeTrendData} margin={{
              top: 20,
              right: 30,
              left: 20,
              bottom: 5
            }} data-id="element-15942">
                <CartesianGrid strokeDasharray="3 3" data-id="element-15943" />
                <XAxis dataKey="month" data-id="element-15944" />
                <YAxis data-id="element-15945" />
                <Tooltip data-id="element-15946" />
                <Line type="monotone" dataKey="time" stroke="#10b981" activeDot={{
                r: 8
              }} data-id="element-15947" />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </div>
      </div>
      {/* User Productivity Table */}
      <div className="bg-white shadow overflow-hidden sm:rounded-md" data-id="element-15948">
        <div className="px-4 py-5 border-b border-gray-200 sm:px-6" data-id="element-15949">
          <h3 className="text-lg leading-6 font-medium text-gray-900" data-id="element-15950">
            User Productivity Metrics
          </h3>
          <p className="mt-1 max-w-2xl text-sm text-gray-500" data-id="element-15951">
            Performance metrics by user for the selected time period
          </p>
        </div>
        <div className="overflow-x-auto" data-id="element-15952">
          <table className="min-w-full divide-y divide-gray-200" data-id="element-15953">
            <thead className="bg-gray-50" data-id="element-15954">
              <tr data-id="element-15955">
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-15956">
                  User
                </th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-15957">
                  Department
                </th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-15958">
                  Tasks Completed
                </th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-15959">
                  Avg. Processing Time
                </th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-15960">
                  Exception Rate
                </th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-15961">
                  Performance
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200" data-id="element-15962">
              {userData.map(user => <tr key={user.id} data-id="element-15963">
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900" data-id="element-15964">
                    {user.name}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500" data-id="element-15965">
                    {user.department}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500" data-id="element-15966">
                    {user.tasksCompleted}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500" data-id="element-15967">
                    {user.avgProcessingTime} days
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500" data-id="element-15968">
                    {user.exceptionRate}%
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap" data-id="element-15969">
                    <div className="flex items-center" data-id="element-15970">
                      {user.avgProcessingTime < 2.0 ? <TrendingUpIcon className="h-5 w-5 text-green-500 mr-1.5" data-id="element-15971" /> : user.avgProcessingTime < 2.5 ? <CheckCircleIcon className="h-5 w-5 text-blue-500 mr-1.5" data-id="element-15972" /> : <AlertCircleIcon className="h-5 w-5 text-yellow-500 mr-1.5" data-id="element-15973" />}
                      <span className={`text-sm font-medium ${user.avgProcessingTime < 2.0 ? 'text-green-700' : user.avgProcessingTime < 2.5 ? 'text-blue-700' : 'text-yellow-700'}`} data-id="element-15974">
                        {user.avgProcessingTime < 2.0 ? 'Excellent' : user.avgProcessingTime < 2.5 ? 'Good' : 'Needs Improvement'}
                      </span>
                    </div>
                  </td>
                </tr>)}
            </tbody>
          </table>
        </div>
      </div>
    </div>;
}